// ============================================================
// types/psicologia.types.ts
// Atenciones psicológicas + Derivaciones + Casos de convivencia.
// ============================================================

// ── Atenciones psicológicas ──────────────────────────────────

export type TipoAtencion = 'ESTUDIANTE' | 'FAMILIAR' | 'MIXTA';

export type MotivoCategoria =
  | 'ANSIEDAD' | 'DEPRESION' | 'BULLYING' | 'FAMILIAR'
  | 'ACADEMICO' | 'CONDUCTA' | 'DUELO' | 'ORIENTACION_VOCACIONAL' | 'OTRO';

export type EstadoAtencion = 'PROGRAMADA' | 'REALIZADA' | 'CANCELADA';

export const TIPO_ATENCION_LABEL: Record<TipoAtencion, string> = {
  ESTUDIANTE: 'Estudiante',
  FAMILIAR:   'Familiar',
  MIXTA:      'Mixta',
};

export const MOTIVO_CATEGORIA_LABEL: Record<MotivoCategoria, string> = {
  ANSIEDAD:               'Ansiedad',
  DEPRESION:              'Depresión',
  BULLYING:               'Bullying',
  FAMILIAR:               'Familiar',
  ACADEMICO:              'Académico',
  CONDUCTA:               'Conducta',
  DUELO:                  'Duelo',
  ORIENTACION_VOCACIONAL: 'Orientación vocacional',
  OTRO:                   'Otro',
};

export const ESTADO_ATENCION_LABEL: Record<EstadoAtencion, string> = {
  PROGRAMADA: 'Programada',
  REALIZADA:  'Realizada',
  CANCELADA:  'Cancelada',
};

export const ESTADO_ATENCION_COLOR: Record<EstadoAtencion, string> = {
  PROGRAMADA: 'bg-blue-100 text-blue-700 border-blue-200',
  REALIZADA:  'bg-green-100 text-green-700 border-green-200',
  CANCELADA:  'bg-slate-100 text-slate-500 border-slate-200',
};

export interface AtencionPsicologicaPayload {
  matriculaId:           number;
  fechaHora:             string;          // ISO local: "2026-05-05T10:00:00"
  duracionMin:           number;
  tipo:                  TipoAtencion;
  motivoCategoria:       MotivoCategoria;
  motivoDescripcion?:    string;
  observaciones?:        string;          // notas internas (no se comparten con familia)
  resumenParaFamilia?:   string;
  compartirConFamilia:   boolean;
  estado?:               EstadoAtencion;  // default PROGRAMADA
}

export interface AtencionPsicologicaResponse {
  idAtencionPsicologica: number;
  matriculaId:           number;
  estudianteId:          number;
  estudianteNombres:     string;
  estudianteApellidos:   string;
  estudianteDni:         string;
  estudianteFotoPerfil:  string | null;
  gradoNombre:           string;
  seccionNombre:         string;
  nivel:                 string;

  psicologoId:           number;
  psicologoNombres:      string;
  psicologoApellidos:    string;

  fechaHora:             string;
  duracionMin:           number;
  tipo:                  TipoAtencion;
  estado:                EstadoAtencion;

  // Campos sensibles — pueden venir null si el rol no tiene permiso
  motivoCategoria:       MotivoCategoria | null;
  motivoDescripcion:     string | null;
  observaciones:         string | null;
  resumenParaFamilia:    string | null;
  compartirConFamilia:   boolean | null;

  fechaRegistro:         string;
  fechaActualizacion:    string | null;
}

// ── Derivaciones psicológicas ────────────────────────────────

export type PrioridadDerivacion = 'BAJA' | 'MEDIA' | 'ALTA' | 'URGENTE';
export type EstadoDerivacion    = 'PENDIENTE' | 'ACEPTADA' | 'RECHAZADA' | 'ATENDIDA';

export const PRIORIDAD_LABEL: Record<PrioridadDerivacion, string> = {
  BAJA:    'Baja',
  MEDIA:   'Media',
  ALTA:    'Alta',
  URGENTE: 'Urgente',
};

export const PRIORIDAD_COLOR: Record<PrioridadDerivacion, string> = {
  BAJA:    'bg-slate-100 text-slate-600 border-slate-200',
  MEDIA:   'bg-amber-100 text-amber-700 border-amber-200',
  ALTA:    'bg-orange-100 text-orange-700 border-orange-200',
  URGENTE: 'bg-red-100 text-red-700 border-red-200',
};

/** Orden numérico para sortear (URGENTE primero). */
export const PRIORIDAD_RANK: Record<PrioridadDerivacion, number> = {
  URGENTE: 4, ALTA: 3, MEDIA: 2, BAJA: 1,
};

export const ESTADO_DERIVACION_LABEL: Record<EstadoDerivacion, string> = {
  PENDIENTE:  'Pendiente',
  ACEPTADA:   'Aceptada',
  RECHAZADA:  'Rechazada',
  ATENDIDA:   'Atendida',
};

export const ESTADO_DERIVACION_COLOR: Record<EstadoDerivacion, string> = {
  PENDIENTE:  'bg-amber-100 text-amber-700 border-amber-200',
  ACEPTADA:   'bg-blue-100 text-blue-700 border-blue-200',
  RECHAZADA:  'bg-slate-100 text-slate-500 border-slate-200',
  ATENDIDA:   'bg-green-100 text-green-700 border-green-200',
};

export interface DerivacionPayload {
  matriculaId: number;
  motivo:      string;
  prioridad?:  PrioridadDerivacion;       // default MEDIA
}

export interface DerivacionEstadoPayload {
  estado:                  'ACEPTADA' | 'RECHAZADA' | 'ATENDIDA';
  observacionesPsicologo?: string;
  atencionVinculadaId?:    number;        // requerido si estado === 'ATENDIDA'
}

export interface DerivacionResponse {
  idDerivacion:          number;
  matriculaId:           number;
  estudianteId:          number;
  estudianteNombres:     string;
  estudianteApellidos:   string;
  estudianteDni:         string;
  estudianteFotoPerfil:  string | null;
  gradoNombre:           string;
  seccionNombre:         string;
  nivel:                 string;

  derivadoPorId:         number;
  derivadoPorNombres:    string;
  derivadoPorApellidos:  string;
  derivadoPorRol:        string;

  motivo:                string;
  prioridad:             PrioridadDerivacion;
  estado:                EstadoDerivacion;
  observacionesPsicologo: string | null;
  atencionVinculadaId:   number | null;

  fechaRegistro:         string;
  fechaActualizacion:    string | null;
}

// ── Casos de convivencia ─────────────────────────────────────

export type TipoIncidente =
  | 'FISICA' | 'PSICOLOGICA' | 'SEXUAL' | 'CIBERBULLYING'
  | 'HURTO'  | 'DISCRIMINACION' | 'ARMAS_DROGAS' | 'OTRO';

export type GravedadCaso = 'LEVE' | 'MODERADA' | 'GRAVE';

export type EstadoCaso = 'ABIERTO' | 'EN_TRATAMIENTO' | 'CERRADO';

export type RolActor = 'AGRESOR' | 'VICTIMA' | 'TESTIGO';

export const TIPO_INCIDENTE_LABEL: Record<TipoIncidente, string> = {
  FISICA:         'Violencia física',
  PSICOLOGICA:    'Violencia psicológica',
  SEXUAL:         'Violencia sexual',
  CIBERBULLYING:  'Ciberbullying',
  HURTO:          'Hurto / robo',
  DISCRIMINACION: 'Discriminación',
  ARMAS_DROGAS:   'Armas / drogas',
  OTRO:           'Otro',
};

export const GRAVEDAD_LABEL: Record<GravedadCaso, string> = {
  LEVE:     'Leve',
  MODERADA: 'Moderada',
  GRAVE:    'Grave',
};

export const GRAVEDAD_COLOR: Record<GravedadCaso, string> = {
  LEVE:     'bg-green-100 text-green-700 border-green-200',
  MODERADA: 'bg-amber-100 text-amber-700 border-amber-200',
  GRAVE:    'bg-red-100 text-red-700 border-red-200',
};

export const ESTADO_CASO_LABEL: Record<EstadoCaso, string> = {
  ABIERTO:        'Abierto',
  EN_TRATAMIENTO: 'En tratamiento',
  CERRADO:        'Cerrado',
};

export const ESTADO_CASO_COLOR: Record<EstadoCaso, string> = {
  ABIERTO:        'bg-red-100 text-red-700 border-red-200',
  EN_TRATAMIENTO: 'bg-amber-100 text-amber-700 border-amber-200',
  CERRADO:        'bg-slate-100 text-slate-500 border-slate-200',
};

export const ROL_ACTOR_LABEL: Record<RolActor, string> = {
  AGRESOR: 'Agresor',
  VICTIMA: 'Víctima',
  TESTIGO: 'Testigo',
};

export const ROL_ACTOR_COLOR: Record<RolActor, string> = {
  AGRESOR: 'bg-red-100 text-red-700 border-red-200',
  VICTIMA: 'bg-blue-100 text-blue-700 border-blue-200',
  TESTIGO: 'bg-slate-100 text-slate-600 border-slate-200',
};

export interface ActorCasoPayload {
  matriculaId: number;
  rolActor:    RolActor;
}

export interface ActorCasoResponse {
  idActor:               number;
  matriculaId:           number;
  estudianteId:          number;
  estudianteNombres:     string;
  estudianteApellidos:   string;
  estudianteDni:         string;
  estudianteFotoPerfil:  string | null;
  gradoNombre:           string;
  seccionNombre:         string;
  rolActor:              RolActor;
}

export interface CasoConvivenciaPayload {
  fechaIncidente:    string;            // YYYY-MM-DD
  tipoIncidente:     TipoIncidente;
  descripcion:       string;
  gravedad:          GravedadCaso;
  accionesTomadas?:  string;
  responsableId?:    number;
  actores:           ActorCasoPayload[];
}

export interface CasoEstadoPayload {
  estado:               EstadoCaso;
  accionesTomadas?:     string;
  compromisoFamiliar?:  string;
}

export interface CasoConvivenciaResponse {
  idCaso:                number;
  fechaIncidente:        string;
  tipoIncidente:         TipoIncidente;
  descripcion:           string;
  gravedad:              GravedadCaso;
  estado:                EstadoCaso;
  accionesTomadas:       string | null;
  compromisoFamiliar:    string | null;

  responsableId:         number | null;
  responsableNombres:    string | null;
  responsableApellidos:  string | null;

  reportadoPorId:        number;
  reportadoPorNombres:   string;
  reportadoPorApellidos: string;
  reportadoPorRol:       string;

  reportadoSiseVe:       boolean;
  fechaReporteSiseVe:    string | null;
  fechaCierre:           string | null;

  actores:               ActorCasoResponse[];

  fechaRegistro:         string;
  fechaActualizacion:    string | null;
}

export interface CasoFiltros {
  estado?:   EstadoCaso;
  gravedad?: GravedadCaso;
  desde?:    string;            // YYYY-MM-DD
  hasta?:    string;
}
